import api from './api';
import { API_BASE_URL } from '../config/constants';

/**
 * سرویس مرکز پایش.
 *
 * استریم زنده عمداً با `fetch` + ReadableStream خوانده می‌شود، نه با
 * `EventSource`. دلیلش امنیتی است: `EventSource` هدر دلخواه نمی‌پذیرد و
 * مجبور می‌شدیم توکن را روی query string بگذاریم — جایی که در لاگ دسترسی
 * nginx، هدر Referer و تاریخچهٔ مرورگر ته‌نشین می‌شود. با fetch، توکن همان‌جا
 * که باید بماند می‌ماند: در هدر Authorization.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ErrorSource = 'backend' | 'web' | 'admin' | 'mobile' | 'watchdog' | 'cron' | 'worker';
export type ErrorLevel = 'fatal' | 'error' | 'warning';
export type ErrorStatus = 'open' | 'resolved' | 'ignored';
export type AlertSeverity = 'critical' | 'error' | 'warning' | 'info';

export interface HealthCheck {
  ok: boolean;
  latencyMs?: number;
  error?: string;
}

export interface SystemSnapshot {
  at: string;
  bootId?: string;
  bootedAt?: string;
  uptimeSec: number;
  healthy?: boolean;
  checks: { mongodb?: HealthCheck; redis?: HealthCheck; elasticsearch?: HealthCheck };
  eventLoopP99Ms: number;
  heapUsedMb: number;
  heapTotalMb?: number;
  rssMb: number;
  diskUsedPercent?: number;
  load1?: number;
  cores?: number;
  systemFreeMb?: number;
  systemTotalMb?: number;
  nodeVersion?: string;
  pid?: number;
  hostname?: string;
  inFlight?: number;
  openIncidents?: Array<{ key: string; title: string; severity: AlertSeverity; firstFiredAt: number }>;
}

export interface RouteMetric {
  route: string;
  requests: number;
  errors5xx: number;
  errors4xx: number;
  avgMs: number;
  p95Ms: number;
  maxMs: number;
  errorRate: number;
}

export interface HttpMetrics {
  since: string;
  uptimeSec: number;
  inFlight: number;
  totals: { requests: number; errors5xx: number; errors4xx: number; avgMs: number };
  last5min: { requests: number; errors5xx: number; ratio: number };
  timeline: Array<{ at: string; requests: number; errors5xx: number; errors4xx: number; avgMs: number; maxMs: number }>;
  slowestRoutes: RouteMetric[];
  failingRoutes: RouteMetric[];
  busiestRoutes: RouteMetric[];
}

export interface AlertIncident {
  _id: string;
  key: string;
  rule: string;
  severity: AlertSeverity;
  title: string;
  summary: string;
  details: Record<string, unknown>;
  status: 'firing' | 'resolved' | 'acknowledged';
  firstFiredAt: string;
  lastFiredAt: string;
  resolvedAt?: string | null;
  durationSec?: number | null;
  occurrences: number;
  notifications: Array<{ channel: string; at: string; ok: boolean; error?: string; phase: string }>;
}

export interface ErrorGroup {
  _id: string;
  fingerprint: string;
  source: ErrorSource;
  level: ErrorLevel;
  type: string;
  message: string;
  culprit?: string;
  environment: string;
  release?: string;
  count: number;
  firstSeenAt: string;
  lastSeenAt: string;
  dailyCounts: Record<string, number>;
  affectedUserCount: number;
  status: ErrorStatus;
  note?: string;
  alertCount: number;
  tags?: Record<string, string>;
}

export interface ErrorSample {
  at: string;
  message: string;
  stack?: string;
  requestId?: string;
  userId?: string | null;
  userRole?: string;
  method?: string;
  route?: string;
  path?: string;
  statusCode?: number;
  ip?: string;
  userAgent?: string;
  release?: string;
  context?: Record<string, unknown>;
}

export interface ErrorGroupDetail extends ErrorGroup {
  samples: ErrorSample[];
  affectedUsers: Array<{ _id: string; firstName?: string; lastName?: string; phoneNumber?: string }>;
}

export interface Overview {
  generatedAt: string;
  system: SystemSnapshot;
  process: { bootId: string; bootedAt: string; uptimeSec: number; nodeVersion: string; pid: number; env?: string };
  http: HttpMetrics;
  incidents: { open: number; list: AlertIncident[] };
  errors: { last24h: number; lastHour: number; openGroups: number; top: ErrorGroup[] };
  alerting: {
    enabled: boolean;
    recipients: string[];
    smtpConfigured: boolean;
    reminderMinutes: number;
    maxEmailsPerHour: number;
    pendingCount: number;
  };
  logs: { totalBytes: number; fileCount: number; dir: string | null };
}

export interface LogLine {
  timestamp?: string;
  level?: string;
  message?: string;
  requestId?: string;
  route?: string;
  status?: number;
  durationMs?: number;
  userId?: string;
  stack?: string;
  [key: string]: unknown;
}

export interface HealthSample {
  at: string;
  healthy: boolean;
  bootId: string;
  eventLoopP99Ms: number;
  heapUsedMb: number;
  rssMb: number;
  diskUsedPercent?: number;
  http: { requests: number; errors5xx: number; errors4xx: number; avgMs: number; p95Ms: number };
}

export interface UptimeReport {
  days: number;
  uptimePercent: number;
  downtimeSec: number;
  incidentCount: number;
  timeline: Array<{
    id: string;
    rule: string;
    title: string;
    startedAt: string;
    endedAt: string | null;
    durationSec: number;
    ongoing: boolean;
  }>;
}

interface Paginated<T> {
  items: T[];
  pagination: { total: number; page: number; pages: number; limit: number };
}

const BASE = '/admin/monitoring';

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

const monitoringService = {
  /**
   * ⚠️ `api.get` خودش پوستهٔ `{status, data}` را باز می‌کند و مغزِ پاسخ را
   * برمی‌گرداند. پس اینجا `res.data.data` نداریم — هرچه برگشت، خودِ داده است.
   * به همین دلیل هم صفحه‌بندی در بک‌اند *داخل* payload گذاشته شده، وگرنه
   * همین‌جا دور ریخته می‌شد.
   */
  getOverview(): Promise<Overview> {
    return api.get<Overview>(`${BASE}/overview`);
  },

  getSnapshot(): Promise<SystemSnapshot> {
    return api.get<SystemSnapshot>(`${BASE}/snapshot`);
  },

  getMetrics(): Promise<HttpMetrics> {
    return api.get<HttpMetrics>(`${BASE}/metrics`);
  },

  getHealthHistory(hours = 24): Promise<{ hours: number; count: number; restarts: string[]; samples: HealthSample[] }> {
    return api.get(`${BASE}/health-history`, { params: { hours } });
  },

  getUptime(days = 30): Promise<UptimeReport> {
    return api.get<UptimeReport>(`${BASE}/uptime`, { params: { days } });
  },

  listErrors(params: {
    page?: number;
    limit?: number;
    source?: string;
    level?: string;
    status?: string;
    search?: string;
    sort?: string;
  }): Promise<Paginated<ErrorGroup>> {
    return api.get<Paginated<ErrorGroup>>(`${BASE}/errors`, { params });
  },

  getErrorDetail(id: string): Promise<ErrorGroupDetail> {
    return api.get<ErrorGroupDetail>(`${BASE}/errors/${id}`);
  },

  updateErrorStatus(id: string, status: ErrorStatus, note?: string): Promise<ErrorGroup> {
    return api.patch<ErrorGroup>(`${BASE}/errors/${id}`, { status, note });
  },

  deleteErrorGroup(id: string): Promise<void> {
    return api.delete<void>(`${BASE}/errors/${id}`);
  },

  getErrorStats(days = 7): Promise<{
    days: number;
    bySource: Array<{ _id: string; groups: number; events: number }>;
    byLevel: Array<{ _id: string; groups: number; events: number }>;
    daily: Array<{ date: string; count: number }>;
  }> {
    return api.get(`${BASE}/errors-stats`, { params: { days } });
  },

  listIncidents(params: { page?: number; limit?: number; status?: string; severity?: string }): Promise<Paginated<AlertIncident>> {
    return api.get<Paginated<AlertIncident>>(`${BASE}/incidents`, { params });
  },

  acknowledgeIncident(id: string, note?: string): Promise<AlertIncident> {
    return api.patch<AlertIncident>(`${BASE}/incidents/${id}/acknowledge`, { note });
  },

  sendTestAlert(): Promise<{ ok: boolean; recipients: string[]; error?: string }> {
    return api.post(`${BASE}/test-alert`);
  },

  getLogFiles(): Promise<{
    files: Array<{ name: string; channel: string; date: string; sizeBytes: number; modifiedAt: string }>;
    usage: { totalBytes: number; fileCount: number; dir: string | null };
  }> {
    return api.get(`${BASE}/logs/files`);
  },

  tailLogs(params: {
    channel?: string;
    date?: string;
    limit?: number;
    level?: string;
    search?: string;
    requestId?: string;
    userId?: string;
    since?: string;
  }): Promise<{ lines: LogLine[]; file: string | null; totalScanned: number }> {
    return api.get(`${BASE}/logs/tail`, { params });
  },

  traceRequest(requestId: string, date?: string): Promise<{ requestId: string; lines: LogLine[] }> {
    return api.get(`${BASE}/logs/trace/${encodeURIComponent(requestId)}`, { params: { date } });
  },

  /**
   * اتصال به استریم زندهٔ وضعیت (SSE روی fetch).
   *
   * برمی‌گرداند: تابع قطع اتصال. اتصال مجدد را *صفحه* مدیریت می‌کند، نه این
   * تابع — چون تصمیم «دوباره وصل شو یا برو سراغ پولینگ» به وضعیت UI بستگی دارد.
   */
  openLiveStream(
    onSnapshot: (data: { at: string; system: SystemSnapshot | null; http: HttpMetrics; openIncidents: unknown[] }) => void,
    onError?: (err: unknown) => void,
    intervalMs = 5000
  ): () => void {
    const controller = new AbortController();
    const token = localStorage.getItem('accessToken') || '';

    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}${BASE}/live?intervalMs=${intervalMs}`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
          signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new Error(`stream failed: ${res.status}`);

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // رویدادهای SSE با یک خط خالی از هم جدا می‌شوند
          let boundary = buffer.indexOf('\n\n');
          while (boundary !== -1) {
            const chunk = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            boundary = buffer.indexOf('\n\n');

            const dataLines = chunk
              .split('\n')
              .filter((l) => l.startsWith('data:'))
              .map((l) => l.slice(5).trim());
            if (dataLines.length === 0) continue; // کامنت ping

            try {
              onSnapshot(JSON.parse(dataLines.join('\n')));
            } catch {
              /* یک فریم خراب نباید صفحه را بشکند */
            }
          }
        }
        // استریم بدون خطا تمام شد (سرور ری‌استارت شد) — صفحه باید دوباره وصل شود
        if (!controller.signal.aborted) onError?.(new Error('stream ended'));
      } catch (err) {
        if (!controller.signal.aborted) onError?.(err);
      }
    })();

    return () => controller.abort();
  },
};

export default monitoringService;
